import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, ListChecks, Lightbulb, CheckCircle } from '@phosphor-icons/react';

export default function HowItWorksPage() {
  const { t } = useLanguage();

  const steps = [
    {
      number: 1,
      icon: ListChecks,
      title: t('howItWorks.steps')[0].title,
      description: t('howItWorks.steps')[0].description,
      details: t('howItWorks.steps')[0].details
    },
    {
      number: 2,
      icon: Lightbulb,
      title: t('howItWorks.steps')[1].title,
      description: t('howItWorks.steps')[1].description,
      details: t('howItWorks.steps')[1].details
    },
    {
      number: 3,
      icon: CheckCircle,
      title: t('howItWorks.steps')[2].title,
      description: t('howItWorks.steps')[2].description,
      details: t('howItWorks.steps')[2].details
    }
  ];

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="pp-container">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <h1 className="pp-heading-1 text-pp-text mb-4" data-testid="how-it-works-title">
                {t('howItWorks.title')}
              </h1>
              <p className="text-xl text-pp-text-secondary">{t('howItWorks.subtitle')}</p>
            </motion.div>

            {/* Steps */}
            <div className="space-y-12 mb-16">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15 }}
                  className="pp-card"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-pp-navy flex items-center justify-center">
                        <span className="text-2xl font-heading font-bold text-white">
                          {step.number}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <step.icon size={24} className="text-pp-navy" weight="duotone" />
                        <h2 className="pp-heading-3 text-pp-text">{step.title}</h2>
                      </div>
                      <p className="text-pp-text-secondary mb-4">{step.description}</p>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-pp-text-secondary">
                            <CheckCircle size={16} className="text-pp-success-text flex-shrink-0" weight="fill" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <Link 
                to="/signup" 
                className="pp-btn-primary inline-flex items-center gap-2"
                data-testid="how-it-works-cta"
              >
                {t('hero.cta')}
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
